/**
 * hooks/usePref.ts
 * Small localStorage-backed preferences hooks.
 */

import { useEffect, useState } from 'react'

/**
 * useBoolPref
 * Boolean preference persisted to localStorage with a namespaced key.
 */
export function useBoolPref(key: string, initial: boolean) {
  const storageKey = `pref:${key}`
  const [value, setValue] = useState<boolean>(() => {
    try {
      const raw = localStorage.getItem(storageKey)
      if (raw === '1') return true
      if (raw === '0') return false
      return initial
    } catch {
      return initial
    }
  })

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, value ? '1' : '0')
    } catch {
      // ignore
    }
  }, [storageKey, value])

  return [value, setValue] as const
}

/**
 * useStringPref
 * String preference persisted to localStorage with a namespaced key.
 */
export function useStringPref(key: string, initial: string) {
  const storageKey = `pref:${key}`
  const [value, setValue] = useState<string>(() => {
    try {
      const raw = localStorage.getItem(storageKey)
      if (typeof raw === 'string') return raw
      return initial
    } catch {
      return initial
    }
  })

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, value)
    } catch {
      // ignore
    }
  }, [storageKey, value])

  return [value, setValue] as const
}
